import { z } from "zod";
import { CONTRACT_KINDS, type ContractKind, type ContractStatus, type TorStatus } from "../enums";
import { LIMITS } from "../limits";
import {
  type ActorRef,
  type UserRef,
  cents,
  checkDateOrder,
  emailOrEmpty,
  isoDate,
  optionalText,
  requiredText,
  uuid,
} from "./common";
import type { Expert } from "./experts";
import type { TorSummary } from "./tors";
import type { ContractDetailV2Fields, MoneyV2Fields } from "./v1-additions";

// ---------- Requests ----------

const cofinancingPct = z
  .number({ invalid_type_error: "Enter a percentage." })
  .min(0, "Must be between 0 and 100.")
  .max(LIMITS.cofinancingPctMax, "Must be between 0 and 100.")
  .refine((n) => Number.isInteger(Math.round(n * 100 * 1e6) / 1e6), "Use at most 2 decimals.");

const contractFields = {
  name: requiredText(LIMITS.contractName, "Name"),
  reference: requiredText(LIMITS.contractReference, "Reference"),
  kind: z.enum(CONTRACT_KINDS),
  funder: optionalText(LIMITS.funder, "Funder"),
  funderContactName: optionalText(LIMITS.funderContactName, "Contact name"),
  funderContactEmail: emailOrEmpty,
  partners: z.array(requiredText(LIMITS.partner, "Partner")).max(LIMITS.partnersMax, "At most 20 partners."),
  description: optionalText(LIMITS.description, "Description"),
  startDate: isoDate.nullable(),
  endDate: isoDate.nullable(),
  envelopeCents: cents(LIMITS.envelopeCentsMax).nullable(),
  cofinancingPct: cofinancingPct.nullable(),
  leadUserId: uuid.nullable(),
  torBoilerplate: optionalText(LIMITS.boilerplate, "Clauses"),
};

export const createContractBody = z
  .object({
    name: contractFields.name,
    reference: contractFields.reference,
    kind: contractFields.kind,
    funder: contractFields.funder.default(""),
    funderContactName: contractFields.funderContactName.default(""),
    funderContactEmail: contractFields.funderContactEmail.default(""),
    partners: contractFields.partners.default([]),
    description: contractFields.description.default(""),
    startDate: contractFields.startDate.default(null),
    endDate: contractFields.endDate.default(null),
    envelopeCents: contractFields.envelopeCents.default(null),
    cofinancingPct: contractFields.cofinancingPct.default(null),
    leadUserId: contractFields.leadUserId.default(null),
    /** Omitted = copied from the organisation's default clauses. */
    torBoilerplate: contractFields.torBoilerplate.optional(),
  })
  .strict()
  .superRefine(checkDateOrder);
export type CreateContractBody = z.input<typeof createContractBody>;
export type CreateContractInput = z.output<typeof createContractBody>;

export const updateContractBody = z
  .object({
    name: contractFields.name.optional(),
    reference: contractFields.reference.optional(),
    kind: contractFields.kind.optional(),
    funder: contractFields.funder.optional(),
    funderContactName: contractFields.funderContactName.optional(),
    funderContactEmail: contractFields.funderContactEmail.optional(),
    partners: contractFields.partners.optional(),
    description: contractFields.description.optional(),
    startDate: contractFields.startDate.optional(),
    endDate: contractFields.endDate.optional(),
    envelopeCents: contractFields.envelopeCents.optional(),
    cofinancingPct: contractFields.cofinancingPct.optional(),
    leadUserId: contractFields.leadUserId.optional(),
    torBoilerplate: contractFields.torBoilerplate.optional(),
  })
  .strict()
  .superRefine(checkDateOrder);
export type UpdateContractBody = z.infer<typeof updateContractBody>;

export const contractNoteBody = z
  .object({ note: optionalText(LIMITS.contractNote, "Note").optional() })
  .strict();
export type ContractNoteBody = z.infer<typeof contractNoteBody>;

export const contractListQuery = z
  .object({ q: z.string().trim().max(LIMITS.searchQuery).optional() })
  .strict();
export type ContractListQuery = z.infer<typeof contractListQuery>;

// ---------- Responses ----------

export type Money = {
  envelopeCents: number | null;
  /** Σ budget of approved ToRs */
  approvedCents: number;
  /** Σ budget of signed_off + submitted + revision */
  onHoldCents: number;
  /** Σ budget of draft ToRs */
  draftCents: number;
  /** envelope − approved − onHold; null when envelope is null; may be negative */
  remainingCents: number | null;
} & MoneyV2Fields;

/** All six keys always present. */
export type TorCounts = Record<TorStatus, number>;

export type ContractSummary = {
  id: string;
  name: string;
  reference: string;
  kind: ContractKind;
  funder: string;
  partners: string[];
  startDate: string | null;
  endDate: string | null;
  status: ContractStatus;
  lead: UserRef | null;
  money: Money;
  torCounts: TorCounts;
  /** = torCounts.submitted */
  waitingCount: number;
  updatedAt: string;
};

export type ContractDetail = ContractSummary & {
  funderContactName: string;
  funderContactEmail: string;
  description: string;
  cofinancingPct: number | null;
  torBoilerplate: string;
  closedAt: string | null;
  closedBy: ActorRef | null;
  /** oldest submittedAt among submitted ToRs */
  oldestSubmittedAt: string | null;
  /** mean(decidedAt − submittedAt) over approved ToRs, 1 decimal */
  averageApprovalDays: number | null;
  /** ceil(averageApprovalDays) or 6 when null; minimum 1 */
  waitThresholdDays: number;
  /** mayActAsLead for the current user */
  isLead: boolean;
  can: { edit: boolean; close: boolean; reopen: boolean; createTor: boolean; manageExperts: boolean };
  /** by name */
  experts: Expert[];
  /** all, including withdrawn, newest first by createdAt */
  tors: TorSummary[];
  createdAt: string;
} & ContractDetailV2Fields;
